<!--
  HomePage · 首页（路由 /）
  - 学习进度概览：课程包数 / 总课时 / 总句数 / 总时长
  - 最近课程（快速跳转播放）
  - 正在处理中的任务提示（upload_step < 4 的课程）
-->
<template>
  <div class="home-page">
    <div class="page-head">
      <h2>🏠 学习首页</h2>
      <button class="primary" @click="$router.push('/packs')">📚 进入课程包</button>
    </div>

    <!-- 进度统计卡片 -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-num">{{ stats.packCount }}</div>
        <div class="stat-label">课程包</div>
      </div>
      <div class="stat-card">
        <div class="stat-num">{{ stats.lessonCount }}</div>
        <div class="stat-label">总课时</div>
      </div>
      <div class="stat-card">
        <div class="stat-num">{{ stats.learnedCount }} <span class="stat-sub">/ {{ stats.lessonCount }}</span></div>
        <div class="stat-label">已学课时</div>
      </div>
      <div class="stat-card">
        <div class="stat-num">{{ stats.sentenceCount }}</div>
        <div class="stat-label">总句数</div>
      </div>
      <div class="stat-card">
        <div class="stat-num">{{ fmtDuration(stats.totalDuration) }}</div>
        <div class="stat-label">总时长</div>
      </div>
    </div>

    <!-- 正在处理 -->
    <section v-if="processing.length" class="section">
      <div class="section-head"><b>⏳ 正在处理中</b></div>
      <div class="processing-list">
        <div v-for="l in processing" :key="l.id" class="proc-item">
          <span class="proc-title">{{ l.title }}</span>
          <span class="step-chip" :class="stepClass(l.upload_step)">{{ stepText(l.upload_step) }}</span>
        </div>
      </div>
    </section>

    <!-- 最近课程 -->
    <section class="section">
      <div class="section-head">
        <b>🎬 最近课程</b>
        <button @click="loadData">↻ 刷新</button>
      </div>
      <div v-if="loading" class="empty">加载中…</div>
      <div v-else-if="!recent.length" class="empty">
        还没有课程，去 <a @click="$router.push('/packs')">课程包</a> 上传第一个视频吧 🚀
      </div>
      <div v-else class="recent-list">
        <div v-for="l in recent" :key="l.id" class="recent-item" @click="onPlay(l)">
          <div class="recent-main">
            <div class="recent-title">{{ l.title || '未命名' }}</div>
            <div class="recent-meta">
              <span>{{ l.sentence_count || 0 }} 句</span>
              <span>·</span>
              <span>{{ fmtDuration(l.duration_sec || 0) }}</span>
              <span v-if="(l.upload_step || 0) < 4" class="step-chip" :class="stepClass(l.upload_step)">
                {{ stepText(l.upload_step) }}
              </span>
              <span v-else-if="l.learned" class="learn-tag learned">✅ 已学</span>
              <span v-else-if="(l.playback_position || 0) > 1" class="learn-tag learning">
                📖 {{ Math.round((l.playback_position / (l.duration_sec || 1)) * 100) }}%
              </span>
            </div>
          </div>
          <button v-if="(l.upload_step || 0) >= 4" class="play-btn">▶ 播放</button>
          <span v-else class="mute">处理中…</span>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { api, toast } from '@/composables/usePlayerState'

const router = useRouter()
const loading = ref(true)
const packs = ref([])
const lessons = ref([])

const stats = computed(() => ({
  packCount: packs.value.length,
  lessonCount: lessons.value.length,
  learnedCount: lessons.value.filter(l => l.learned).length,
  sentenceCount: lessons.value.reduce((s, l) => s + (l.sentence_count || 0), 0),
  totalDuration: lessons.value.reduce((s, l) => s + (l.duration_sec || 0), 0),
}))

// 最近的课程（按创建时间倒序，取前 10）
const recent = computed(() => {
  return [...lessons.value]
    .sort((a, b) => (b.created_at || 0) - (a.created_at || 0))
    .slice(0, 10)
})

// 正在处理的课程
const processing = computed(() => {
  return lessons.value.filter(l => {
    const s = l.upload_step || 0
    return s > 0 && s < 4
  })
})

async function loadData() {
  loading.value = true
  try {
    const [pk, ls] = await Promise.all([
      api('/api/course-packs'),
      api('/api/lessons'),
    ])
    packs.value = pk.items || []
    lessons.value = ls.items || []
  } catch (e) {
    toast('加载失败：' + e.message)
  } finally {
    loading.value = false
  }
}

function onPlay(l) {
  if ((l.upload_step || 0) < 4) {
    toast('该课程尚未处理完成')
    return
  }
  router.push('/lessons/' + l.id + '/play')
}

function fmtDuration(sec) {
  sec = Math.max(0, sec | 0)
  const h = (sec / 3600) | 0
  const m = ((sec % 3600) / 60) | 0
  const s = sec % 60
  if (h > 0) return `${h}h${String(m).padStart(2, '0')}m`
  if (m > 0) return `${m}m${String(s).padStart(2, '0')}s`
  return `${s}s`
}

function stepText(step) {
  return ['⏳ 待处理', '🎵 抽音频', '🗣 ASR', '📝 格式化', '✅ 完成'][step] || '未知'
}
function stepClass(step) {
  if (step >= 4) return 'done'
  if (step >= 1) return 'running'
  return 'pending'
}

onMounted(() => {
  loadData()
  // 若有处理中的任务，每 5s 刷新一次
  setInterval(() => {
    if (processing.value.length) loadData()
  }, 5000)
})
</script>

<style scoped>
.home-page { display: flex; flex-direction: column; gap: 18px; }
.page-head {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
}
.page-head h2 { margin: 0; font-size: 22px; }

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 14px;
}
.stat-card {
  padding: 22px 18px;
  border: 1px solid var(--line);
  border-radius: 14px;
  background: var(--panel);
  backdrop-filter: blur(10px);
  text-align: center;
}
.stat-num {
  font-size: 30px; font-weight: 700;
  background: linear-gradient(135deg, var(--accent), var(--accent2));
  -webkit-background-clip: text; background-clip: text;
  -webkit-text-fill-color: transparent;
}
.stat-label { color: var(--mute); font-size: 13px; margin-top: 4px; }

.section {
  padding: 16px; border: 1px solid var(--line); border-radius: 12px;
  background: var(--panel); backdrop-filter: blur(10px);
}
.section-head {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 12px;
}
.section-head b { font-size: 15px; }

.empty {
  padding: 30px; text-align: center; color: var(--mute);
  border: 1px dashed var(--line); border-radius: 10px;
}
.empty a { color: var(--accent); cursor: pointer; text-decoration: underline; }

.processing-list, .recent-list { display: flex; flex-direction: column; gap: 8px; }
.proc-item {
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 12px; border: 1px solid var(--line); border-radius: 8px;
  background: #0d163655;
}
.proc-title { font-size: 14px; }

.recent-item {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 14px; border: 1px solid var(--line); border-radius: 10px;
  background: #0d163655; cursor: pointer; transition: all .15s ease;
}
.recent-item:hover {
  border-color: var(--accent);
  background: #1a2550;
}
.recent-title { font-size: 14px; font-weight: 500; }
.recent-meta {
  display: flex; gap: 6px; align-items: center;
  color: var(--mute); font-size: 12px; margin-top: 4px;
}
.play-btn { padding: 6px 14px; }
.mute { color: var(--mute); font-size: 12px; }

.step-chip {
  display: inline-block; padding: 2px 8px; border-radius: 999px;
  font-size: 11px; white-space: nowrap;
}
.step-chip.pending { background: #1a2550; color: var(--mute); }
.step-chip.running { background: #4a3a0e; color: var(--warn); }
.step-chip.done    { background: #0e3a1f; color: var(--good); }

.learn-tag {
  display: inline-block; padding: 2px 8px; border-radius: 999px;
  font-size: 11px; white-space: nowrap;
}
.learn-tag.learned   { background: #0e3a1f; color: var(--good); }
.learn-tag.learning  { background: #4a3a0e; color: var(--warn); }

.stat-sub { font-size: 16px; opacity: 0.6; }
</style>
